import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, UserPlus } from 'lucide-react';
import { acceptFriendRequestAction, rejectFriendRequestAction } from '@/lib/friends/mutations';
import { toast } from 'sonner';
import type { Notification } from '@/lib/db/queries/notifications';

interface FriendRequestNotificationProps {
  notification: Notification;
  onUpdate: () => void;
}

export function FriendRequestNotification({ notification, onUpdate }: FriendRequestNotificationProps) {
  const [loading, setLoading] = useState(false);
  const requesterId = notification.metadata?.requester_id;
  const requestId = notification.metadata?.request_id;

  const handleAccept = async () => {
    if (!requestId) return;
    
    setLoading(true);
    try {
      await acceptFriendRequestAction(requestId);
      toast.success('Friend request accepted!');
      onUpdate();
    } catch (error: any) {
      toast.error(error.message || 'Failed to accept friend request');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!requestId) return;
    
    setLoading(true);
    try {
      await rejectFriendRequestAction(requestId);
      toast.success('Friend request rejected');
      onUpdate();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject friend request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`p-4 hover:bg-arc-panel-soft transition-colors ${
        !notification.read ? 'bg-arc-panel-soft' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-arc-panel-soft border border-arc-border flex items-center justify-center flex-shrink-0">
          <UserPlus className="w-5 h-5 text-arc-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-arc-text-main text-sm">{notification.title}</div>
          {notification.body && (
            <div className="text-xs text-arc-text-muted mt-1">{notification.body}</div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleAccept}
              disabled={loading}
              className="gap-2 h-8"
            >
              <Check className="w-4 h-4" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleReject}
              disabled={loading}
              className="gap-2 h-8"
            >
              <X className="w-4 h-4" />
              Reject
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

